import { StyleSheet, View } from 'react-native';
import { Box, Text } from '../../../components/primitives';
import { useResponsive } from '../../../hooks/useResponsive';
import { colors } from '../../../theme/colors';
import type { AgentStatusEntry } from '../types';
import { AgentCard } from './AgentCard';

const INTERNAL_AGENT = 'flowsellai_company_research';

const DEFAULT_AGENTS: AgentStatusEntry[] = [
  { type: 'linkedin_company_research', displayName: 'LinkedIn', status: 'idle' },
  { type: 'browser_company_research', displayName: 'Web', status: 'idle' },
  { type: 'instagram_company_research', displayName: 'Instagram', status: 'idle' },
];

interface AgentStatusPanelProps {
  readonly agents?: AgentStatusEntry[];
  readonly compact?: boolean;
}

export function AgentStatusPanel({ agents, compact }: AgentStatusPanelProps) {
  const { isMobile } = useResponsive();
  const visibleAgents = (agents?.length ? agents : DEFAULT_AGENTS).filter(
    (a) => a.type !== INTERNAL_AGENT,
  );
  const useCompact = compact ?? isMobile;

  if (useCompact) {
    return (
      <Box
        flexDirection="row"
        alignItems="center"
        paddingX="xl"
        paddingY="md"
        borderBottomWidth={1}
        borderBottomColor={colors.border.subtle}
      >
        <Text variant="caption" color={colors.text.tertiary} style={styles.compactLabel}>
          Agents
        </Text>
        <View style={styles.compactChipRow}>
          {visibleAgents.map((agent, index) => (
            <View
              key={agent.type}
              style={index < visibleAgents.length - 1 ? styles.compactChipSpacing : undefined}
            >
              <AgentCard
                agentType={agent.type}
                displayName={agent.displayName}
                status={agent.status}
                resultCount={agent.resultCount}
                compact
              />
            </View>
          ))}
        </View>
      </Box>
    );
  }

  return (
    <Box padding="lg" gap="md">
      <Text variant="bodySmall" color={colors.text.secondary} style={styles.panelTitle}>
        Research Agents
      </Text>
      <Box gap="sm">
        {visibleAgents.map((agent) => (
          <AgentCard
            key={agent.type}
            agentType={agent.type}
            displayName={agent.displayName}
            status={agent.status}
            resultCount={agent.resultCount}
          />
        ))}
      </Box>
    </Box>
  );
}

const styles = StyleSheet.create({
  panelTitle: {
    fontWeight: '600',
    textTransform: 'uppercase' as const,
    letterSpacing: 0.5,
    fontSize: 11,
  },
  compactLabel: {
    fontSize: 10,
    fontWeight: '600',
    textTransform: 'uppercase' as const,
    marginRight: 16,
  },
  compactChipRow: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  compactChipSpacing: {
    marginRight: 12,
  },
});
